import React, { useState, useEffect } from "react";
import { useStyles } from "./modalContent";
import notification from "../../common/Alert";

const ReusableAdminModal = ({
  isOpen,
  onClose,
  mode,
  providerData,
  onSubmit,
  onDelete,
}) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    first_name: providerData?.first_name || "",
    last_name: providerData?.last_name || "",
    email: providerData?.email || "",
    phone_number: providerData?.phone_number || "",
    password: "",
  });
  const [error, setError] = useState(null);

  // Update formData when providerData changes (for edit mode)
  useEffect(() => {
    if (providerData && (mode === "edit" || mode === "view")) {
      setFormData({
        first_name: providerData.first_name || "",
        last_name: providerData.last_name || "",
        email: providerData.email || "",
        phone_number: providerData.phone_number || "",
        password: "", // Password is not pre-filled for security
      });
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        password: "",
      });
    }
  }, [providerData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null); // Clear error on input change
  };

  const validateForm = () => {
    if (!formData.first_name) {
      setError("First name is required.");
      return false;
    }
    if (!formData.last_name) {
      setError("Last name is required.");
      return false;
    }
    if (!formData.email) {
      setError("Email is required.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Invalid email format.");
      return false;
    }
    if (!formData.phone_number) {
      setError("Phone number is required.");
      return false;
    }
    if (mode === "create" && !formData.password) {
      setError("Password is required for creating an admin.");
      return false;
    }
    if (formData.password && formData.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);
    if ((mode === "edit" || mode === "create") && !validateForm()) return;

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: formData.phone_number,
        ...(formData.password && { password: formData.password }), // Only include password if provided
      };
      if (mode === "edit" || mode === "create") {
        await onSubmit(providerData?.id, payload);
        notification.notication(
          "success",
          mode === "create"
            ? "Admin created successfully"
            : "Admin updated successfully",
          "Success",
          { timer: 3000 }
        );
      } else if (mode === "delete") {
        await onDelete(providerData.id);
        notification.notication(
          "success",
          "Admin deleted successfully",
          "Success",
          { timer: 3000 }
        );
      }
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Operation failed. Please try again.";
      setError(errorMessage);
      notification.notication("error", errorMessage, "Error", { timer: 5000 });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: "1000",
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px",
          boxShadow: "0 8px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h2>
          {mode === "view"
            ? "View Admin"
            : mode === "edit"
            ? "Edit Admin"
            : mode === "create"
            ? "Create Admin"
            : "Delete Admin"}
        </h2>
        {error && <p style={{ color: "red" }}>{error}</p>}

        {mode === "view" && (
          <div>
            <p>
              <strong>Name:</strong> {providerData?.first_name}{" "}
              {providerData?.last_name}
            </p>
            <p>
              <strong>Email:</strong> {providerData?.email || "N/A"}
            </p>
            <p>
              <strong>Phone Number:</strong>{" "}
              {providerData?.phone_number || "N/A"}
            </p>
          </div>
        )}

        {(mode === "edit" || mode === "create") && (
          <form>
            <div>
              <label className={classes.enroleeIdText}>First Name:</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={classes.enroleeInputId}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "8px",
                  marginBottom: "16px",
                }}
              />
            </div>
            <div>
              <label className={classes.enroleeIdText}>Last Name:</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={classes.enroleeInputId}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "8px",
                  marginBottom: "16px",
                }}
              />
            </div>
            <div>
              <label className={classes.enroleeIdText}>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={classes.enroleeInputId}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "8px",
                  marginBottom: "16px",
                }}
              />
            </div>
            <div>
              <label className={classes.enroleeIdText}>Phone Number:</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                className={classes.enroleeInputId}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "8px",
                  marginBottom: "16px",
                }}
              />
            </div>
            <div>
              <label className={classes.enroleeIdText}>
                {mode === "edit" ? "New Password (optional):" : "Password:"}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={classes.enroleeInputId}
                required={mode === "create"}
                style={{
                  width: "100%",
                  padding: "8px",
                  marginTop: "8px",
                  marginBottom: "16px",
                }}
              />
            </div>
          </form>
        )}

        {mode === "delete" && (
          <p>
            Are you sure you want to delete the admin "
            {providerData?.first_name} {providerData?.last_name}"?
          </p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              background: "#ccc",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>
          {(mode === "edit" || mode === "create" || mode === "delete") && (
            <button
              onClick={handleSubmit}
              style={{
                padding: "8px 16px",
                background: mode === "delete" ? "#d32f2f" : "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {mode === "edit"
                ? "Save"
                : mode === "create"
                ? "Create"
                : "Delete"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReusableAdminModal;
